import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, useReducedMotion } from 'motion/react'
import { ArrowRight, Check, Hourglass, RotateCcw, X } from 'lucide-react'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { cn } from '@/lib/utils'
import type { Card } from '@/types/entities'

/**
 * Stage-change requests, the UI half.
 *
 * Moves the normal flow refuses (anything out of Completed, or off the stage graph) can be
 * ASKED for: the person picks where it should go and says why, and the item's approver approves
 * (the move is applied) or declines. The drawer shows a pending request to everyone, with the
 * decision buttons for whoever can decide; a refused drag on the board offers the same request.
 */

type Status = Card['status']
const LABEL: Record<Status, string> = { todo: 'To do', in_progress: 'In progress', blocked: 'Blocked', in_review: 'Approval pending', done: 'Completed' }

export interface StageRequest {
  id: string
  from: Status
  to: Status
  reason: string
  requestedBy: string
  requestedByEmail: string
  requestedAt: string
  deciderIds: string[]
  status: 'pending' | 'approved' | 'declined'
  decidedBy: string | null
  decidedAt: string | null
  note: string | null
}

const key = (cardId: string) => ['stage-requests', cardId] as const

export function useStageRequests(cardId: string | null | undefined) {
  return useQuery({
    queryKey: key(cardId ?? ''),
    queryFn: () => apiGet<{ items: StageRequest[]; deciderIds: string[] }>(`cards/${cardId}/stage-requests`),
    enabled: Boolean(cardId),
    staleTime: 30_000,
  })
}

/** Ask from anywhere (e.g. a refused drag): the host in the app shell opens the dialog. */
export function requestStageChange(cardId: string, to?: Status) {
  window.dispatchEvent(new CustomEvent('tapwork:stage-request', { detail: { cardId, to } }))
}

/** Stages one might ask to move to: anything but where it is now, and never Blocked (that needs its own form). */
const targetsFor = (card: Pick<Card, 'status'>): Status[] =>
  (['todo', 'in_progress', 'in_review', 'done'] as Status[]).filter((s) => s !== card.status)

export function StageRequestDialog({ card, initialTo, open, onOpenChange }: { card: Pick<Card, 'id' | 'status' | 'title' | 'code'>; initialTo?: Status; open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient()
  const targets = targetsFor(card)
  const [to, setTo] = useState<Status>(initialTo && targets.includes(initialTo) ? initialTo : card.status === 'done' ? 'in_review' : targets[0]!)
  const [reason, setReason] = useState('')
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const send = useMutation({
    mutationFn: () => apiPost<{ request: StageRequest; card?: { status: Status } }>(`cards/${card.id}/stage-requests`, { to, reason: reason.trim() }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: key(card.id) })
      void queryClient.invalidateQueries({ queryKey: qk.card(card.id) })
      void queryClient.invalidateQueries({ queryKey: ['cards'] })
      toast.success(res.card ? `Moved to ${LABEL[res.card.status]}` : 'Request sent to the approver', {
        description: res.card ? 'You’re an approver, so it was applied right away.' : 'You’ll be notified when they decide.',
      })
      onOpenChange(false)
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          reasonRef.current?.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCcw className="size-5 text-brand" /> Ask to change the stage</DialogTitle>
          <DialogDescription>
            {card.code ? <span className="num font-semibold text-foreground">{card.code}</span> : null}{card.code ? ' · ' : ''}{card.title}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Move from <span className="text-foreground">{LABEL[card.status]}</span> to</p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Move to">
              {targets.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={to === s}
                  onClick={() => setTo(s)}
                  className={cn('rounded-full border px-3 py-1.5 text-sm font-medium transition-colors', to === s ? 'border-brand bg-brand text-text-inverse' : 'border-border text-text-secondary hover:border-brand/40 hover:text-foreground')}
                >
                  {LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Why</p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Moved to Completed by mistake — proof is attached, needs sign-off."
              ref={reasonRef}
              rows={3}
              maxLength={1000}
            />
          </div>
          <p className="text-tiny text-text-muted">The item’s approver gets this and decides. If they approve, it moves; if not, it stays where it is.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => send.mutate()} disabled={reason.trim().length < 3 || send.isPending}>{send.isPending ? 'Sending…' : 'Send request'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Listens for `requestStageChange(...)` from anywhere and opens the dialog for that item. */
export function StageRequestHost() {
  const [target, setTarget] = useState<{ cardId: string; to?: Status } | null>(null)
  useEffect(() => {
    const on = (e: Event) => setTarget((e as CustomEvent<{ cardId: string; to?: Status }>).detail)
    window.addEventListener('tapwork:stage-request', on)
    return () => window.removeEventListener('tapwork:stage-request', on)
  }, [])
  const card = useQuery({
    queryKey: qk.card(target?.cardId ?? ''),
    queryFn: () => apiGet<Card>(`cards/${target!.cardId}`),
    enabled: Boolean(target?.cardId),
  })
  if (!target || !card.data) return null
  return <StageRequestDialog key={target.cardId} card={card.data} initialTo={target.to} open onOpenChange={(o) => !o && setTarget(null)} />
}

/**
 * The drawer's panel: a pending request (with Approve / Decline for deciders), the last
 * decision, or, for a completed item, the way to ask.
 */
export function StageRequestPanel({ card, people, canAsk }: { card: Pick<Card, 'id' | 'status' | 'title' | 'code'>; people: Record<string, { displayName: string } | undefined>; canAsk: boolean }) {
  const reduce = useReducedMotion()
  const { userId } = useIdentity()
  const queryClient = useQueryClient()
  const requests = useStageRequests(card.id)
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  const [openedAt] = useState(() => Date.now())
  const pending = requests.data?.items.find((r) => r.status === 'pending')
  const last = requests.data?.items.find((r) => r.status !== 'pending')
  const canDecide = Boolean(pending && userId && (pending.deciderIds.includes(userId) || requests.data?.deciderIds.includes(userId)))
  const name = (id: string | null) => (id ? people[id]?.displayName ?? 'Someone' : 'Someone')

  const decide = useMutation({
    mutationFn: (outcome: 'approve' | 'decline') => apiPost(`cards/${card.id}/stage-requests/${pending!.id}/${outcome}`, { note: note.trim() || undefined }),
    onSuccess: (_res, outcome) => {
      void queryClient.invalidateQueries({ queryKey: key(card.id) })
      void queryClient.invalidateQueries({ queryKey: qk.card(card.id) })
      void queryClient.invalidateQueries({ queryKey: ['cards'] })
      toast.success(outcome === 'approve' ? `Approved — moved to ${LABEL[pending!.to]}` : 'Declined — the item stays where it is')
      setNote('')
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  })

  if (!pending && !(canAsk && card.status === 'done') && !(last && openedAt - Date.parse(last.decidedAt ?? last.requestedAt) < 3 * 86_400_000)) return null

  return (
    <>
      {pending ? (
        <motion.section
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-warning/40 bg-warning-soft p-4"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Hourglass className="size-4 text-warning" />
            {name(pending.requestedBy)} asks to move this
            <span className="inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 text-xs">{LABEL[pending.from]} <ArrowRight className="size-3" /> {LABEL[pending.to]}</span>
          </p>
          <p className="mt-1.5 text-sm text-text-secondary">“{pending.reason}”</p>
          {canDecide ? (
            <div className="mt-3 grid gap-2">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)" maxLength={1000} className="bg-card" />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => decide.mutate('approve')} disabled={decide.isPending}><Check className="size-3.5" /> Approve & move</Button>
                <Button size="sm" variant="outline" onClick={() => decide.mutate('decline')} disabled={decide.isPending}><X className="size-3.5" /> Decline</Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-tiny text-text-muted">Waiting on {pending.deciderIds.map((id) => name(id)).join(', ')}.</p>
          )}
        </motion.section>
      ) : last && openedAt - Date.parse(last.decidedAt ?? last.requestedAt) < 3 * 86_400_000 ? (
        <p className={cn('rounded-xl border px-4 py-2.5 text-sm', last.status === 'approved' ? 'border-success/30 bg-success-soft text-success' : 'border-border bg-surface-sunken text-text-secondary')}>
          {name(last.decidedBy)} {last.status} the request to move this to {LABEL[last.to]}{last.note ? ` — “${last.note}”` : '.'}
        </p>
      ) : null}
      {!pending && canAsk && card.status === 'done' ? (
        <button
          type="button"
          onClick={() => setAsking(true)}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-border px-4 py-2.5 text-left text-sm text-text-secondary transition-colors hover:border-brand/50 hover:text-foreground"
        >
          <span className="flex items-center gap-2"><RotateCcw className="size-4" /> Completed by mistake? Ask the approver to move it back.</span>
          <ArrowRight className="size-4" />
        </button>
      ) : null}
      {asking ? <StageRequestDialog card={card} open onOpenChange={setAsking} /> : null}
    </>
  )
}
